'use client';

import styles from './Question.module.css';
import { InlineUnsafe, Flex, Text, Pill, Separator } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { useHash } from '@/_hooks/useHash';
import { useMemo, type FC } from 'react';
import type { QuestionProps } from './Question.types';
export const Question: FC<QuestionProps> = ({
  children,
  glossaries,
  hasSecondLevelContinuation,
  id,
  isLastChild,
  isOptional,
  isReviewSection,
  level,
  number,
  status,
  title,
  priorYearValue,
}) => {
  const hash = useHash();
  const questionId: string = useMemo(() => `question${id}`, [id]);

  const questionClasses: string = getClasses(styles.question, {
    [styles.questionCompleted]: status === 'completed',
    [styles.questionPending]: status === 'pending',
    [styles.questionIncomplete]: status === 'incomplete',
    [styles.questionError]: status === 'error',
    [styles.questionLevel1]: level === 1,
    [styles.questionLevel2]: level === 2,
    [styles.questionLevel3]: level === 3,
    [styles.hasSecondLevelContinuation]: hasSecondLevelContinuation,
    [styles.questionLastChild]: isLastChild,
    // NOTE @chris grabbing this directly as :target doesn't always work (assuming bc of nested questions)
    [styles.questionTarget]: hash === questionId,
  });

  const questionNumberContainerClasses: string = getClasses(
    styles.questionNumberContainer,
    {
      [styles.questionNumberContainerReview]: isReviewSection,
    },
  );

  // NOTE @chris Question title contains more than just WYSWYG-supported elements
  const isExtremeHTML: boolean = useMemo(
    () =>
      title.includes('/table') ||
      title.includes('/strong') ||
      title.includes('/b'),
    [title],
  );

  return (
    <Flex gap={6} className={questionClasses} id={questionId}>
      <Flex isCentered className={questionNumberContainerClasses}>
        <Text
          type="small"
          className={styles.questionNumber}
          fontWeight="semi-bold"
          color={status === 'completed' ? 'success' : 'secondary'}
        >
          {number}
        </Text>
      </Flex>
      <Flex isColumn gap={12} className={styles.questionContent}>
        <Flex isBetween gap={4} isYCentered={true}>
          <Flex isColumn gap={4}>
            <Text
              type="div"
              fontWeight={isExtremeHTML ? undefined : 'semi-bold'}
              data-test-id="QUESTION.TEXT"
            >
              <InlineUnsafe
                text={title}
                isExtremelyUnsafe
                glossaries={glossaries}
              />
            </Text>
            {priorYearValue && (
              <Flex isYCentered={true} gap={4}>
                <Separator isVertical={true} height={20} isAccent={true} />
                <Text type="small" color="secondary">
                  Previously you answered:
                </Text>
                <Text type="small" color="secondary" isItalic={true}>
                  <InlineUnsafe text={priorYearValue} />
                </Text>
              </Flex>
            )}
          </Flex>
          {isOptional && <Pill intent="default" label="Optional" />}
        </Flex>
        {children}
      </Flex>
    </Flex>
  );
};
